import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateSensorRequestSchema, type CreateSensorRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import { useCreateSensorPosition, type CardSummary } from "@/features/cards/hooks";

interface NewSensorFormProps {
  deviceId: string;
  card: CardSummary;
  onDone: () => void;
}

// Campo vazio → undefined (não NaN): NaN faria z.number().optional() falhar, impedindo
// salvar um sensor analógico SEM escala (válido: "0 de 4"). Campos numéricos.
const numOrUndef = (v: string) => (v === "" ? undefined : Number(v));

function NumField({
  id,
  label,
  reg,
  ph,
  error,
}: {
  id: string;
  label: string;
  reg: object;
  ph: string;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" step="any" placeholder={ph} {...reg} />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function NewSensorForm({ deviceId, card, onDone }: NewSensorFormProps) {
  const create = useCreateSensorPosition(deviceId);
  const isAnalog = card.type === "ANALOG";
  const form = useForm<CreateSensorRequest>({
    resolver: zodResolver(CreateSensorRequestSchema),
    defaultValues: {
      code: "",
      name: "",
      dataType: isAnalog ? "number" : "enum",
      cardId: card.id,
      position: undefined,
      rawMin: undefined,
      rawMax: undefined,
      engMin: undefined,
      engMax: undefined,
      unit: undefined,
    },
  });

  async function onSubmit(values: CreateSensorRequest) {
    try {
      await create.mutateAsync(values);
      toast.success("Posição cadastrada.");
      onDone();
    } catch (err) {
      toast.error("Falha ao cadastrar posição.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  const errs = form.formState.errors;
  return (
    <form
      onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
      className="grid grid-cols-1 gap-3 rounded-md border border-border-subtle bg-bg-muted-rd p-3 sm:grid-cols-3"
    >
      <div className="space-y-1.5">
        <Label htmlFor="ns-code">Código do sensor</Label>
        <Input id="ns-code" {...form.register("code")} placeholder="EXT01-EA03-2" />
        {errs.code && <p className="text-xs text-destructive">{errs.code.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ns-name">Nome</Label>
        <Input id="ns-name" {...form.register("name")} placeholder="Pressão de injeção" />
        {errs.name && <p className="text-xs text-destructive">{errs.name.message}</p>}
      </div>
      <NumField
        id="ns-pos"
        label="Posição (base 0, R→L)"
        ph="0"
        reg={form.register("position", { setValueAs: numOrUndef })}
        error={errs.position?.message}
      />
      {isAnalog && (
        <>
          <NumField
            id="ns-rawmin"
            label="Raw mín"
            ph="0"
            reg={form.register("rawMin", { setValueAs: numOrUndef })}
            error={errs.rawMin?.message}
          />
          <NumField
            id="ns-rawmax"
            label="Raw máx"
            ph="1000"
            reg={form.register("rawMax", { setValueAs: numOrUndef })}
            error={errs.rawMax?.message}
          />
          <div className="space-y-1.5">
            <Label htmlFor="ns-unit">Unidade</Label>
            <Input id="ns-unit" {...form.register("unit")} placeholder="bar" />
          </div>
          <NumField
            id="ns-engmin"
            label="Eng mín"
            ph="0"
            reg={form.register("engMin", { setValueAs: numOrUndef })}
            error={errs.engMin?.message}
          />
          <NumField
            id="ns-engmax"
            label="Eng máx"
            ph="100"
            reg={form.register("engMax", { setValueAs: numOrUndef })}
            error={errs.engMax?.message}
          />
        </>
      )}
      <div className="flex gap-2 sm:col-span-3">
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Salvando…" : "Adicionar posição"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
